import { ThemeContext } from '@/theme/ThemeProvider.tsx';
import { ConfigProvider, Spin } from 'antd';
import React, { lazy, Suspense, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter,
  Routes as ReactRoutes,
  Route,
  Navigate,
} from 'react-router-dom';
import { ThemeProvider, StyleSheetManager } from 'styled-components';
import { darkTheme, lightTheme } from '../styles/Qtheme';
import { View } from '../styles/reactnative';
import { GlobalStyle } from './../styles/global-styles';
import { NotFoundPage } from './components/NotFoundPage/Loadable';
import { ErrorBoundary } from './components/ErrorBoundary';
import Layout from './components/QBIT/Layout';
import { useToken } from './hooks';
import AdditionalInfoPage from './pages/AdditionalInfoPage';
import Logout from './pages/Logout';
import * as PAGE from './pagesImport';
import ScrollToTop from '@utils/ScrollToTop.tsx';
import UnloadLog from '@/utils/UnloadLog';
import { DEFAULT_EP_ID } from '@/app/constants/DEFAULT';
import { MMRPage } from '@app/pages/MMRPage';
import { CopyTradePage } from './pages/CopyTradePage';
import CopyTradingPage from './pages/CopyTradingPage';
import { MyFollowsPage } from './pages/CopyTradePage/MyFollowsPage';
import { MyPortfolioPage } from './pages/CopyTradePage/MyPortfolioPage';
import { MyFollowersPage } from './pages/CopyTradePage/MyFollowersPage';
import { EarningsPage } from './pages/CopyTradePage/EarningsPage';
import { PortfolioDetailPage } from './pages/CopyTradePage/PortfolioDetailPage';
import { OrderHistoryPage } from './pages/CopyTradePage/OrderHistoryPage';
import { SettlementsPage } from './pages/CopyTradePage/SettlementsPage';
import LoadingFallback from './components/PageLoadingFallback';

const Qp2pPage = lazy(() =>
  import('./pages/QBIT/Qp2pPage').then((module) => ({
    default: module.Qp2pSection,
  }))
);
const AboutUsSection = lazy(() =>
  import('./pages/QBIT/AboutUs').then((module) => ({
    default: module.AboutUsSection,
  }))
);
const OriginDepositSection = lazy(() =>
  import('./pages/QBIT/Deposit').then((module) => ({
    default: module.OriginDepositSection,
  }))
);
const DepositPage = lazy(() =>
  import('./pages/QBIT/Deposit').then((module) => ({
    default: module.OriginDepositSection,
  }))
);
const MarketSection = lazy(() =>
  import('./pages/QBIT/Market').then((module) => ({
    default: module.MarketPage,
  }))
);
const BotPayment = lazy(() =>
  import('./pages/QBIT/BotPayment').then((module) => ({
    default: module.BotPayment,
  }))
);
const CopyTradeLayout = lazy(() =>
  import('./pages/CopyTradePage/CopyTradeLayout').then((module) => ({
    default: module.CopyTradeLayout,
  }))
);
const LoanSection = lazy(() =>
  import('./pages/QBIT/Loan').then((module) => ({
    default: module.LoanSection,
  }))
);
const Fund = lazy(() =>
  import('./pages/QBIT/Fund').then((module) => ({
    default: module.Fund,
  }))
);
const Pay = lazy(() =>
  import('./pages/QBIT/Pay').then((module) => ({
    default: module.Pay,
  }))
);
const Earn = lazy(() =>
  import('./pages/QBIT/Earn').then((module) => ({
    default: module.Earn,
  }))
);
const Convert = lazy(() =>
  import('./pages/QBIT/Convert').then((module) => ({
    default: module.Convert,
  }))
);
const TermsSection = lazy(() =>
  import('./pages/QBIT/Terms').then((module) => ({
    default: module.TermsPage,
  }))
);
const GuideSection = lazy(() =>
  import('./pages/QBIT/Guide').then((module) => ({
    default: module.Guide,
  }))
);
const InstitutionalSection = lazy(() =>
  import('./pages/QBIT/Institutional').then((module) => ({
    default: module.InstitutionalSection,
  }))
);
const ZMobileTradingView = lazy(() =>
  import('./pages/ZMobileTradingView').then((module) => ({
    default: module.ZMobileTradingView,
  }))
);
const TradePage = lazy(() =>
  import('./pages/TradePage').then((module) => ({
    default: module.TradePage,
  }))
);
const DemoTradePageWrapper = lazy(() =>
  import('./pages/DemoTradePage/DemoTradePageWrapper').then((module) => ({
    default: module.DemoTradePageWrapper,
  }))
);
const TradeCompetition = lazy(() =>
  import('./pages/TradeCompetition').then((module) => ({
    default: module.TradeCompetition,
  }))
);

const TradingCompetitionUser = lazy(
  () => import('./pages/TradingCompetitionUser')
);
const ReferralPage = lazy(() =>
  import('./pages/ReferralPage').then((module) => ({
    default: module.ReferralSection,
  }))
);
const OrdersPage = lazy(() =>
  import('./pages/OrdersPage').then((module) => ({
    default: module.OrdersPage,
  }))
);
const RewardPage = lazy(() =>
  import('./pages/RewardPage').then((module) => ({
    default: module.RewardPage,
  }))
);
const MarketplacePage = lazy(() =>
  import('./pages/BotTradingPage/MarketplacePage').then((module) => ({
    default: module.MarketplacePage,
  }))
);
const MyBotsPage = lazy(() =>
  import('./pages/BotTradingPage/MyBotsPage').then((module) => ({
    default: module.MyBotsPage,
  }))
);
const BotDetailPage = lazy(() =>
  import('./pages/BotTradingPage/BotDetailPage').then((module) => ({
    default: module.BotDetailPage,
  }))
);
const CreateBotPage = lazy(() =>
  import('./pages/BotTradingPage/CreateBotPage').then((module) => ({
    default: module.CreateBotPage,
  }))
);
const TradingHistoryPage = lazy(() =>
  import('./pages/BotTradingPage/TradingHistoryPage').then((module) => ({
    default: module.TradingHistoryPage,
  }))
);
// New Design Pages for /trading-bots/*
const NewMyBotsPage = lazy(() =>
  import('./pages/TradingBotsPage/NewMyBotsPage').then((module) => ({
    default: module.NewMyBotsPage,
  }))
);
const NewBotDetailPage = lazy(() =>
  import('./pages/TradingBotsPage/NewBotDetailPage').then((module) => ({
    default: module.NewBotDetailPage,
  }))
);
const NewCreateBotPage = lazy(() =>
  import('./pages/TradingBotsPage/NewCreateBotPage').then((module) => ({
    default: module.NewCreateBotPage,
  }))
);
const NewTradingHistoryPage = lazy(() =>
  import('./pages/TradingBotsPage/NewTradingHistoryPage').then((module) => ({
    default: module.NewTradingHistoryPage,
  }))
);
const TopTradersPage = lazy(() =>
  import('./pages/TopTradersPage').then((module) => ({
    default: module.TopTradersPage,
  }))
);
// SubscriptionsPage merged into MarketplacePage (Binance style)
const AssetPage = lazy(() =>
  import('./pages/AssetPage').then((module) => ({
    default: module.AssetPage,
  }))
);
const QJoinPage = lazy(() =>
  import('./pages/QBIT/QJoinPage').then((module) => ({
    default: module.QJoinPage,
  }))
);
const ForgetPwPage = lazy(() =>
  import('./pages/QBIT/ForgetPw').then((module) => ({
    default: module.ForgetPw,
  }))
);
const BuySellCryptoPage = lazy(() =>
  import('./pages/QBIT/BuySellCrypto').then((module) => ({
    default: module.BuySellCryptoPage,
  }))
);
const BuyCryptoPage = lazy(() =>
  import('./pages/QBIT/BuyCrypto').then((module) => ({
    default: module.BuyCryptoPage,
  }))
);
const PnlCalendarPage = lazy(() =>
  import('./pages/PnlCalendarPage').then((module) => ({
    default: module.PnlCalendarPage,
  }))
);
const FearGreedIndexPage = lazy(() =>
  import('./pages/FearGreedIndex').then((module) => ({
    default: module.FearGreedIndexPage,
  }))
);
const TradingBotsPage = lazy(() =>
  import('./pages/TradingBotsPage').then((module) => ({
    default: module.TradingBotsPage,
  }))
);
const NoticePage = lazy(() =>
  import('./pages/NoticePage').then((module) => ({
    default: module.NoticePage,
  }))
);
const NewHomePage = lazy(() =>
  import('./pages/NewHomePage').then((module) => ({
    default: module.NewHomePage,
  }))
);
const LoginPage = lazy(() =>
  import('./pages/QBIT/QLoginPage').then((module) => ({
    default: module.QLoginPage,
  }))
);
const MembershipTiersPage = lazy(() => import('./pages/MembershipTiersPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const InstitutionalPage = lazy(() => import('./pages/InstitutionalPage'));

const AccountPage = lazy(() => import('./pages/AccountPage'));

const AffiliateProgramPage = lazy(() => import('./pages/AffiliateProgramPage'));

const SupportPage = lazy(() => import('./pages/SupportPage'));

export function Routes() {
  const { i18n, t } = useTranslation();
  const { data: token } = useToken();
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';
  const [isAppReady, setIsAppReady] = React.useState(false);

  // 앱 초기 마운트 후 Layout 표시
  React.useEffect(() => {
    setIsAppReady(true);
  }, []);

  const useragt = navigator.userAgent.toLowerCase();
  if (useragt.match(/kakaotalk/i)) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          fontSize: 20,
        }}
      >
        Do not support KakaoTalk Browser
      </View>
    );
  }

  const themeToggler = () => {
    toggleTheme();
  };

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <UnloadLog />
        <ScrollToTop />
        <Helmet
          defaultTitle={'PieCrip'}
          titleTemplate="%s"
          htmlAttributes={{ lang: i18n.language }}
        >
          <meta name="description" content={t('title')} />
        </Helmet>
        <ConfigProvider
          theme={{
            components: {
              Input: {
                hoverBg: '#2a2c2e',
                hoverBorderColor: 'rgba(255, 255, 255, 0.12)',
                activeBg: '#2a2c2e',
                activeBorderColor: 'rgba(255, 255, 255, 0.12)',
                activeShadow: 'none',
              },
              Message: {
                zIndexPopup: 20000,
                contentBg: theme === 'light' ? '#ffffff' : '#1f1f1f',
                contentPadding: '12px 16px',
                borderRadiusLG: 8,
              },
              Radio: {
                buttonBg: 'transparent',
                buttonColor: theme === 'light' ? '#212121' : '#fff',
              },
              Table: {
                headerBg:
                  theme === 'light'
                    ? 'rgba(214, 216, 243, 0.6)'
                    : 'rgba(25, 26, 25, 0.6)',
                headerColor: theme === 'light' ? '#212121' : '#fff',
                headerBorderRadius: 0,
                headerSplitColor: 'transparent',
                footerBg: theme === 'light' ? 'rgb(253, 253, 253)' : '#1F1F1F',
                stickyScrollBarBg: theme === 'light' ? '#F6EDEA' : '#191A19',
                borderColor: 'transparent',
              },
              Select: {
                optionActiveBg: 'rgba(0, 110, 73, 0.10)',
                optionSelectedBg: 'rgba(255, 255, 255, 0.05)',
                optionFontSize: 14,
                optionPadding: '12px 20px',
                selectorBg: 'transparent',
                hoverBorderColor: 'transparent',
                activeBorderColor: '#07d993',
                activeOutlineColor: 'transparent',
              },
              Slider: {
                dotSize: 11,
                controlSize: 13,
                dotActiveBorderColor: '#07D993',
                dotBorderColor: '#2A2C2E',
              },
            },
            token: {
              // Seed Token
              // colorPrimary: '#07D993',
              // colorPrimaryHover: '#3394FF',
              // colorPrimaryActive: '#0060D8',
              // colorBorder: theme === 'light' ? '#DDE0F3' : '#333333',
              // colorBgContainer:
              //   theme === 'light' ? 'rgb(253, 253, 253)' : '#1F1F1F',
              // colorText: theme === 'light' ? '#212121' : '#fff',
              // colorBgBase: theme === 'light' ? 'rgb(253, 253, 253)' : '#1F1F1F',
              // colorTextPlaceholder: theme === 'light' ? '#757575' : '#B5B5BC',
              // colorTextDisabled: theme === 'light' ? '#212121' : 'gray',
              // 파이크립은 현재 다크모드만 지원
              colorPrimary: '#07D993',
              colorPrimaryHover: '#07D993',
              colorPrimaryActive: '#07D993',
              colorBorder: '#333333',
              colorBgContainer: '#1F1F1F',
              colorText: '#fff',
              colorBgBase: '#1F1F1F',
              colorTextPlaceholder: '#B5B5BC',
              colorTextDisabled: 'gray',
            },
          }}
        >
          {/* 파이크립은 현재 다크모드만 지원 */}
          {/* <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}> */}{' '}
          <StyleSheetManager disableCSSOMInjection>
            <ThemeProvider theme={darkTheme}>
              <GlobalStyle />
              {!isAppReady ? (
                <LoadingFallback />
              ) : (
                <Layout themeToggler={themeToggler}>
                  <Suspense fallback={<LoadingFallback />}>
                    <ReactRoutes>
                      <Route
                        path="asset"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Assets" />}
                          >
                            {token ? <AssetPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />

                      <Route
                        path="/"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Home" />}
                          >
                            <NewHomePage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="membership-tiers"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Membership Tiers" />
                            }
                          >
                            <MembershipTiersPage />
                          </Suspense>
                        }
                      />
                      {/* P2P Page */}
                      <Route
                        path="p2p"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading p2p" />}
                          >
                            <Qp2pPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="about-us"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading About Us" />
                            }
                          >
                            <AboutUsPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="institutional"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Institutional" />
                            }
                          >
                            <InstitutionalPage />
                          </Suspense>
                        }
                      />

                      <Route
                        path="support"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Support" />
                            }
                          >
                            <SupportPage />
                          </Suspense>
                        }
                      />

                      <Route
                        path="account"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Account" />
                            }
                          >
                            <AccountPage />
                          </Suspense>
                        }
                      />

                      <Route
                        path="affiliate-program"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Referral Program" />
                            }
                          >
                            <AffiliateProgramPage />
                          </Suspense>
                        }
                      />

                      {/* Deposit one-click-buy Page */}
                      <Route
                        path="crypto"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Origin Deposit" />
                            }
                          >
                            <OriginDepositSection />
                          </Suspense>
                        }
                      />
                      {/*<Route*/}
                      {/*  path="one-click-buy"*/}
                      {/*  element={*/}
                      {/*    <Suspense fallback={<div>Loading Deposit...</div>}>*/}
                      {/*      <DepositPage />*/}
                      {/*    </Suspense>*/}
                      {/*  }*/}
                      {/*/>*/}

                      {/* market Page */}
                      <Route
                        path="markets"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Market" />}
                          >
                            <MarketSection />
                          </Suspense>
                        }
                      />

                      {/* Bot Payment Page - Legacy, redirect to new create bot page */}
                      <Route
                        path="bot-payment"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Bot Payment" />
                            }
                          >
                            {token ? <CreateBotPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />

                      {/* Copy Trading Pages */}
                      <Route
                        path="copy-trading"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Copy Trading" />
                            }
                          >
                            <CopyTradingPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="copytrade"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Copy Trade" />
                            }
                          >
                            <CopyTradePage />
                          </Suspense>
                        }
                      />
                      {/* Following (팔로잉) - 새 경로 */}
                      <Route
                        path="copy-trade/following"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Following" />
                            }
                          >
                            {token ? <MyFollowsPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      {/* 기존 경로 호환성 유지 */}
                      <Route
                        path="copy-trade/my-follows"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading My Follows" />
                            }
                          >
                            {token ? <MyFollowsPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      {/* 리더 전용 라우트 */}
                      <Route
                        path="copy-trade/my-portfolio"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading My Portfolio" />
                            }
                          >
                            {token ? <MyPortfolioPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="copy-trade/my-followers"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading My Followers" />
                            }
                          >
                            {token ? <MyFollowersPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="copy-trade/earnings"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Earnings" />
                            }
                          >
                            {token ? <EarningsPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="copy-trade/portfolio/:id"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Portfolio" />
                            }
                          >
                            <PortfolioDetailPage />
                          </Suspense>
                        }
                      />

                      <Route
                        path="copy-trade/orders"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Orders" />}
                          >
                            {token ? <OrderHistoryPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="copy-trade/settlements"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Settlements" />
                            }
                          >
                            {token ? <SettlementsPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />

                      {/* <Route path="crypto" element={<CryptoDepositPage/>}/> */}

                      {/* Loan Page */}
                      <Route
                        path="loan"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Loan" />}
                          >
                            <LoanSection />
                          </Suspense>
                        }
                      />
                      <Route
                        path="funds"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Fund" />}
                          >
                            <Fund />
                          </Suspense>
                        }
                      />

                      {/* Pay Page */}
                      <Route
                        path="pay"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Pay" />}
                          >
                            <Pay />
                          </Suspense>
                        }
                      />

                      {/* Earn Page */}
                      <Route
                        path="earn"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Earn" />}
                          >
                            <Earn />
                          </Suspense>
                        }
                      />

                      {/* Convert Page */}
                      <Route
                        path="convert"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Convert" />
                            }
                          >
                            <Convert />
                          </Suspense>
                        }
                      />

                      <Route
                        path="terms-and-conditions"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Terms" />}
                          >
                            <TermsSection />
                          </Suspense>
                        }
                      />

                      <Route
                        path="guide"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Guide" />}
                          >
                            <GuideSection />
                          </Suspense>
                        }
                      />

                      <Route
                        path="guide/:id"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Guide Detail" />
                            }
                          >
                            <GuideSection />
                          </Suspense>
                        }
                      />

                      <Route
                        path="institutional"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Institutional" />
                            }
                          >
                            <InstitutionalSection />
                          </Suspense>
                        }
                      />

                      <Route
                        path="leverage-margin"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Market" />}
                          >
                            <MMRPage />
                          </Suspense>
                        }
                      />

                      <Route path={'trade'}>
                        <Route
                          index
                          element={
                            <Navigate to={`/trade/${DEFAULT_EP_ID}`} replace />
                          }
                        />
                        <Route
                          path="chart"
                          element={
                            <Suspense
                              fallback={
                                <LoadingFallback text="Loading Trade" />
                              }
                            >
                              <ZMobileTradingView />
                            </Suspense>
                          }
                        >
                          <Route
                            path="*"
                            element={
                              <Suspense
                                fallback={
                                  <LoadingFallback text="Loading Trade" />
                                }
                              >
                                <ZMobileTradingView />
                              </Suspense>
                            }
                          />
                        </Route>
                      </Route>

                      {/*<Route path="/trade/chart" element={<PAGE.ZMobileTradingView />} />*/}
                      {/*<Route*/}
                      {/*  path="/trade/chart/*"*/}
                      {/*  element={<PAGE.ZMobileTradingView />}*/}
                      {/*/>*/}
                      {/* Security Page */}
                      {/* Primary Page */}
                      {/*<Route exact path="/" component={PAGE.HomePage} />*/}

                      {/* Authentication Page */}
                      <Route
                        path="login"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading" />}
                          >
                            {token ? <AccountPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="logout"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading" />}
                          >
                            {token ? <Logout /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="signup/*"
                        element={
                          token ? (
                            <Suspense
                              fallback={<LoadingFallback text="Loading" />}
                            >
                              <AccountPage />
                            </Suspense>
                          ) : (
                            <Suspense
                              fallback={
                                <LoadingFallback text="Loading Sign up" />
                              }
                            >
                              <QJoinPage />
                            </Suspense>
                          )
                        }
                      />
                      <Route
                        path="forget-password/*"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Forget Password" />
                            }
                          >
                            <ForgetPwPage />
                          </Suspense>
                        }
                      />
                      {/* Authentication Page */}

                      {/* Trading View Page */}
                      <Route
                        path="trade/:ep_id"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Trade" />}
                          >
                            <TradePage />
                          </Suspense>
                        }
                      />
                      {/* Demo Trading Page */}
                      <Route
                        path="demo-trade/:ep_id"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Demo Trade" />
                            }
                          >
                            <DemoTradePageWrapper />
                          </Suspense>
                        }
                      />
                      <Route
                        path="trade-competition/:id"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Trade Competition" />
                            }
                          >
                            <TradeCompetition />
                          </Suspense>
                        }
                      />
                      <Route
                        path="trading-competition-user"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Trading Competition User..." />
                            }
                          >
                            <TradingCompetitionUser />
                          </Suspense>
                        }
                      />
                      {/*<Route*/}
                      {/*  path="trade"*/}
                      {/*  element={*/}
                      {/*    <Suspense fallback={<div>Loading Trade...</div>}>*/}
                      {/*      <TradePage />*/}
                      {/*    </Suspense>*/}
                      {/*  }*/}
                      {/*>*/}
                      {/*  <Route*/}
                      {/*    path="*"*/}
                      {/*    element={*/}
                      {/*      <Suspense fallback={<div>Loading Trade...</div>}>*/}
                      {/*        <TradePage />*/}
                      {/*      </Suspense>*/}
                      {/*    }*/}
                      {/*  />*/}
                      {/*</Route>*/}
                      {/* Trading View Page */}

                      {/* Markets  Page */}
                      {/*<Route*/}
                      {/*	exact*/}
                      {/*	path="/markets/"*/}
                      {/*	component={PAGE.MarketPage}*/}
                      {/*/>*/}
                      {/*<Route path="/markets/trends" element={<PAGE.MarketTrendPage />} />*/}
                      {/*<Route path="markets">*/}
                      {/*  <Route path="trends" element={<PAGE.MarketTrendPage />} />*/}
                      {/*</Route>*/}
                      {/* Markets  Page */}
                      <Route
                        path="referral"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Referral" />
                            }
                          >
                            {token ? <ReferralPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />

                      {/* Orders Page */}
                      <Route
                        path="orders"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Orders" />}
                          >
                            <OrdersPage />
                          </Suspense>
                        }
                      />
                      {/* Bot Trading Routes - New Structure */}
                      <Route
                        path="bot-trading"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Bot Trading" />
                            }
                          >
                            <MarketplacePage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="bot-trading/my-bots"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading My Bots" />
                            }
                          >
                            {token ? <MyBotsPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="bot-trading/bot/:id"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Bot Detail" />
                            }
                          >
                            {token ? <BotDetailPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="bot-trading/create"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Create Bot" />
                            }
                          >
                            {token ? <CreateBotPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="bot-trading/edit-bot/:id"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Edit Bot" />
                            }
                          >
                            {token ? <CreateBotPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="bot-trading/history"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Trading History" />
                            }
                          >
                            {token ? <TradingHistoryPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      {/* Subscriptions merged into Marketplace */}

                      {/* Legacy Bot Trading Routes - Redirect to new routes */}
                      <Route
                        path="tradingbot"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Trading Bot" />
                            }
                          >
                            <MarketplacePage />
                          </Suspense>
                        }
                      />
                      {/*<Route path="leaderboard" element={<PAGE.LeaderBoardPage />} />*/}
                      {/*<Route path="copytrade" element={<PAGE.CopyTradePage />} />*/}
                      {/*<Route*/}
                      {/*  path="followertrader"*/}
                      {/*  element={<PAGE.FollowerTraderPage />}*/}
                      {/*/>*/}
                      {/*<Route*/}
                      {/*  path="traderdetail"*/}
                      {/*  element={<PAGE.TraderDetailPage />}*/}
                      {/*/>*/}

                      {/*<Route*/}
                      {/*  path="notifications"*/}
                      {/*  element={<PAGE.NotificationPage />}*/}
                      {/*/>*/}
                      {/*<Route path="notiSetting" element={<PAGE.NotiSettingPage />} />*/}
                      {/*<Route path="abouts" element={<PAGE.AboutsPage />} />*/}
                      {/*<Route path="supports" element={<PAGE.SupportsPage />} />*/}
                      <Route
                        path="settings"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading" />}
                          >
                            {token ? <AccountPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      {/* secoundary Page */}
                      {/* <Route exact path="/about/" component={PAGE.AboutPage} /> */}
                      {/*<Route path="contactus" element={<PAGE.ContactUsPage />} />*/}
                      {/*<Route path="faq" element={<PAGE.FAQPage />} />*/}
                      {/*<Route*/}
                      {/*  path="terms-service"*/}
                      {/*  element={<PAGE.PrivacyPolicyPage />}*/}
                      {/*/>*/}
                      <Route
                        path="additional-info"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Additional Info" />
                            }
                          >
                            <AdditionalInfoPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="buysell-crypto"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Buy Crypto" />
                            }
                          >
                            <BuySellCryptoPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="buy-crypto"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Buy Crypto" />
                            }
                          >
                            <BuyCryptoPage />
                          </Suspense>
                        }
                      />

                      <Route
                        path="notice"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Notice" />}
                          >
                            <NoticePage />
                          </Suspense>
                        }
                      />

                      <Route
                        path="pnl-calendar"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading PnL Calendar" />
                            }
                          >
                            {token ? <PnlCalendarPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />

                      <Route
                        path="fear-greed-index"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Fear & Greed Index" />
                            }
                          >
                            <FearGreedIndexPage />
                          </Suspense>
                        }
                      />

                      <Route
                        path="trading-bots"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Trading Bots" />
                            }
                          >
                            <TradingBotsPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="trading-bots/my-bots"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading My Bots" />
                            }
                          >
                            {token ? <NewMyBotsPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="trading-bots/bot/:id"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Bot Detail" />
                            }
                          >
                            {token ? <NewBotDetailPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="trading-bots/create"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Create Bot" />
                            }
                          >
                            {token ? <NewCreateBotPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="trading-bots/edit/:id"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Edit Bot" />
                            }
                          >
                            {token ? <NewCreateBotPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />
                      <Route
                        path="trading-bots/history"
                        element={
                          <Suspense
                            fallback={
                              <LoadingFallback text="Loading Trading History" />
                            }
                          >
                            {token ? <NewTradingHistoryPage /> : <LoginPage />}
                          </Suspense>
                        }
                      />

                      <Route
                        path="reward"
                        element={
                          <Suspense
                            fallback={<LoadingFallback text="Loading Reward" />}
                          >
                            <RewardPage />
                          </Suspense>
                        }
                      />

                      <Route path="*" element={<NotFoundPage />} />
                    </ReactRoutes>
                  </Suspense>
                </Layout>
              )}
            </ThemeProvider>
          </StyleSheetManager>
        </ConfigProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
